import { Button, Drawer, CircularProgress } from '@mui/material';
import { useCallback } from 'react';
import IconSetting from 'src/assets/images/icons/setting.svg';
import IconClose from 'src/assets/images/icons/close-rounded.svg';
import { useFormContext } from 'react-hook-form';
import { AnalyticSearchRequestDto } from 'src/generated/services/TFinancialApi';
import { nameofFactory } from 'src/utils/nameofFactory';
import { CustomFilterAccordion } from './CustomFilterAccordion';
import { countUnemptyField } from '../analyticsMapper';
import { FilterNum } from './FilterNum';

export interface FilterDrawerProps {
  onChangeFilterNew?(filter: Record<string, any>): void;
  onResetFilter?(): void;
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
  filterAccordions: any[]; // Required prop for filter data
}

interface FilterTriggerButtonProps {
  onClick: () => void;
}

interface FilterDrawerContentProps {
  onChangeFilterNew?(filter: Record<string, any>): void;
  onResetFilter?(): void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  filterAccordions: any[];
  isLoading?: boolean;
}

// Trigger button component
export const FilterTriggerButton = ({ onClick }: FilterTriggerButtonProps) => {
  const { watch } = useFormContext();
  const filtersFieldName = nameofFactory<AnalyticSearchRequestDto>()('filters');
  const formValues = watch(filtersFieldName);

  const FilterNumDisplay = useCallback(() => {
    if (formValues) {
      const filterNum = Object.keys(formValues).reduce((pre, curr) => pre + countUnemptyField(formValues[curr]), 0);
      return <FilterNum filterNum={filterNum} />;
    }
    return null;
  }, [formValues]);

  return (
    <button
      type='button'
      onClick={onClick}
      className='flex items-center justify-between px-12 xl:px-16 py-[8px] border border-[#D3D7DF] rounded-10 gap-8 xl:gap-10'
    >
      <img src={IconSetting} alt='' />
      <div className='flex justify-center items-center gap-[8px]'>
        <p className='text-14 xl:text-16 leading-24'>Filters</p>
        <FilterNumDisplay />
      </div>
    </button>
  );
};

// Drawer content component
export const FilterDrawerContent = ({ onChangeFilterNew, onResetFilter, isOpen, setIsOpen, filterAccordions, isLoading }: FilterDrawerContentProps) => {
  const { resetField, watch } = useFormContext();
  const filtersFieldName = nameofFactory<AnalyticSearchRequestDto>()('filters');
  const formValues = watch(filtersFieldName);

  const FilterNumDisplay = useCallback(() => {
    if (formValues) {
      const filterNum = Object.keys(formValues).reduce((pre, curr) => pre + countUnemptyField(formValues[curr]), 0);
      return <FilterNum filterNum={filterNum} />;
    }
    return null;
  }, [formValues]);

  return (
    <Drawer
      anchor='right'
      open={isOpen}
      ModalProps={{ onBackdropClick: () => setIsOpen(false) }}
      sx={{
        '& .MuiDrawer-paper': {
          backgroundColor: '#f5f6fa',
        },
      }}
    >
      <div className='flex h-full w-[340px] xl:w-[360px] flex-col items-start bg-neutral-10 '>
        <div className='flex w-full px-16 xl:px-20 py-14 xl:py-16 justify-between items-center bg-white border-[#D3D7DF] border-[1px] '>
          <div className='flex gap-6 xl:gap-8 items-center'>
            <p className='text-base xl:text-xl font-bold'>Filters</p>
            <FilterNumDisplay />
          </div>
          <div className='flex items-center gap-12 xl:gap-16'>
            <button
              type='button'
              onClick={() => {
                if (onResetFilter) {
                  onResetFilter();
                }
                resetField(filtersFieldName);
              }}
              className='text-red-40 underline text-12 xl:text-14 hover:cursor-pointer bg-transparent border-0 p-0'
            >
              Reset to Default
            </button>
            <button type='button' onClick={() => setIsOpen(false)}>
              <img src={IconClose} alt='Close' className='w-20 xl:w-24' />
            </button>
          </div>
        </div>
        <div className='p-8 xl:p-12 w-full h-fit flex flex-col gap-12 xl:gap-16'>
          <>
            {isLoading ? (
              <div className='flex items-center justify-center py-16'>
                <CircularProgress color='primary' />
              </div>
            ) : (
              <>
                {filterAccordions?.map((v) => (
                  <CustomFilterAccordion
                    key={`filter-${v.name}`}
                    filtersDetails={v.filtersDetails}
                    icon={v.icon}
                    name={v.name}
                    label={v.label}
                    onChangeFilterNew={onChangeFilterNew}
                  />
                ))}
              </>
            )}
          </>
        </div>
        <div className='mt-auto flex items-center justify-center p-20 xl:p-24 border-t border-[#D3D7DF] w-full'>
          <Button
            variant='outlined'
            fullWidth
            sx={{ backgroundColor: '#E0F3E6', borderColor: '#00992B', textTransform: 'capitalize' }}
            onClick={() => setIsOpen(false)}
          >
            Close Filters
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

// Original FilterDrawer component for backward compatibility
export const FilterDrawer = (props: FilterDrawerProps) => {
  const { onChangeFilterNew, onResetFilter, isOpen, setIsOpen, filterAccordions } = props;

  return (
    <div>
      <FilterTriggerButton onClick={() => setIsOpen(true)} />
      <FilterDrawerContent
        onChangeFilterNew={onChangeFilterNew}
        onResetFilter={onResetFilter}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        filterAccordions={filterAccordions}
      />
    </div>
  );
};
